import * as React from 'react';

import { cn } from '@/lib/utils';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => {
    return (
      <textarea
        className={cn(
          'flex min-h-[80px] w-full rounded-md  bg-background px-3 py-2 ring-offset-background text-[15px] placeholder:text-[##5F5F5F] placeholder:uppercase focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#3f5595] disabled:cursor-not-allowed disabled:opacity-50 focus-within:placeholder:text-[#3f5595]',
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);

Textarea.displayName = 'Textarea';
export interface CustomTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  id: string;
  label?: string;
  error?: string;
  labelStyle?: React.CSSProperties;
}

const CustomTextarea = React.forwardRef<HTMLTextAreaElement, CustomTextareaProps>(
  ({ className, id, placeholder, label, onBlur, error, value, labelStyle, ...props }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [textareaValue, setTextareaValue] = React.useState('');

    const isShrink = React.useMemo(
      () => isFocused || !!textareaValue || !!value,
      [isFocused, textareaValue, value],
    );

    const handleTextareaChange = React.useCallback(
      (element: HTMLTextAreaElement) => {
        if (element) {
          const updateValue = () => setTextareaValue(element.value);
          element.addEventListener('input', updateValue);

          return () => {
            element.removeEventListener('input', updateValue);
          };
        }
      },
      [value],
    );

    const textareaValues = value === '' || value ? { value: value } : null;

    return (
      <div className='relative w-full'>
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'w-11/12 pt-[5px] absolute left-3 pointer-events-none transition-all duration-200  flex flex-col gap-1 justify-center',
              isShrink
                ? 'text-[12px] top-[2px] text-[#d8d8d8]'
                : 'text-[14px] top-[10px] text-[#d8d8d8]',
              placeholder ? 'text-[12px] top-[7px] text-[#d8d8d8]' : '',
            )}
            style={labelStyle}
          >
            {label}
            <span className='-mt-1 text-[14px] text-[#777777]'>
              {value
                ? ''
                : placeholder && typeof placeholder === 'string'
                  ? placeholder.split('\n').map((line, idx) => (
                      <React.Fragment key={idx}>
                        {line}
                        {idx !== placeholder.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))
                  : placeholder}
            </span>
          </label>
        )}
        <textarea
          id={id}
          data-testid={`${id}-testid`}
          aria-label={label}
          onFocus={() => setIsFocused(true)}
          defaultValue=''
          className={cn(
            'flex min-h-[80px] w-full rounded-xl border-[#515764] border-2  bg-background px-3 py-3 pb-0 pt-[28px] text-white text-sm ring-offset-background text-[14px] placeholder:text-[#3b4b6a] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#676767] disabled:cursor-not-allowed disabled:opacity-50',
            className,
            error ? ' border-red focus-visible:ring-red' : '',
          )}
          ref={(element) => {
            if (ref && typeof ref === 'function') {
              ref(element);
            }
            if (element && ref) {
              setTextareaValue(element.value);
              handleTextareaChange(element);
            }
          }}
          onBlur={(e) => {
            onBlur?.(e);
            setIsFocused(false);
          }}
          {...textareaValues}
          {...props}
        />
        {error && (
          <p aria-label='error' aria-errormessage={error} className='text-red text-xs mt-1'>
            {error}
          </p>
        )}
      </div>
    );
  },
);

CustomTextarea.displayName = 'CustomTextarea';

export { CustomTextarea, Textarea };
